/**
 * "Custom CV" catalog for the onboarding AI-input screen.
 *
 * A preset is a named starting point the user picks explicitly before their CV
 * is written. Each one is a `styleSegmentId` understood by
 * `POST /api/onboarding/enrich-cv`, so applying a preset reuses the existing
 * enrichment pipeline rather than adding a second generation path: the matching
 * directive block lives in `apps/api/src/onboarding/cv-styles.ts` and the ids
 * here must stay in sync with it (`cv-presets.test.ts` enforces that).
 *
 * Presets are deliberately separate from `cv-style-segments.ts`. Those segments
 * are auto-suggested from career evidence, so they may never state demographic
 * facts. A preset is chosen by name by the user, which is exactly what makes
 * "UAE National CV" legitimate here and not there.
 */

import type { CVConfig } from '../../lib/cv-mock-data';

export type CvPresetIconKey =
  | 'flag-uae'
  | 'graduation'
  | 'people'
  | 'switch'
  | 'globe';

/**
 * `CVConfig.color` is a palette ID, never a hex string: the renderers resolve it
 * through `COLOR_MAP` in packages/tokens/src/cv-layout.ts and silently fall back
 * to BLACK for anything unrecognised. Typing the ids here makes that contract
 * enforceable instead of discoverable only by looking at the rendered CV.
 */
export type CvPresetColorId =
  | 'PURPLE'
  | 'BLACK'
  | 'BLUE'
  | 'EMERALD'
  | 'RED'
  | 'ORANGE';

export type CvPresetLayoutConfig = Omit<
  Pick<CVConfig, 'style' | 'color' | 'showPhoto'>,
  'color'
> & { color: CvPresetColorId };

export interface CvPreset {
  /**
   * Stable id, also the `styleSegmentId` sent to enrich-cv and the i18n key
   * suffix under `onboarding.cvPreset.presets.<id>`.
   */
  id: string;
  icon: CvPresetIconKey;
  /**
   * Visual treatment applied to the CV config when this preset is applied, so
   * the preview reflects the choice instead of every preset looking identical.
   */
  layout: CvPresetLayoutConfig;
}

/**
 * Default look for a CV with no preset applied.
 *
 * BLACK matches `DEFAULT_CV_CONFIG` in packages/utils/src/cv-config.ts — the
 * canonical default for new or unconfigured CVs (see
 * docs/cv-theme-system-architecture.md) — so the onboarding preview, the saved
 * config, and the exports all start from the same monochrome document.
 */
export const DEFAULT_CV_PRESET_LAYOUT: CvPresetLayoutConfig = {
  style: 'MODERN',
  color: 'BLACK',
  showPhoto: true,
};

/**
 * Order is the order shown in the sheet. Accents map to the same colour the
 * matching entry in the skills catalog declares
 * (`apps/api/src/skills/seed-v2/skills-index.json`), so a preset and its
 * published skill read as the same product: BLACK is #0A0A0A there, BLUE is
 * #2563EB.
 */
export const CV_PRESETS: CvPreset[] = [
  {
    id: 'preset-uae-national',
    icon: 'flag-uae',
    layout: { style: 'MODERN', color: 'BLACK', showPhoto: true },
  },
  {
    id: 'preset-fresh-graduate',
    icon: 'graduation',
    layout: { style: 'MODERN', color: 'BLUE', showPhoto: true },
  },
  {
    id: 'preset-hr',
    icon: 'people',
    layout: { style: 'CLASSIC', color: 'BLACK', showPhoto: false },
  },
  {
    id: 'preset-career-switch',
    icon: 'switch',
    layout: { style: 'MODERN', color: 'PURPLE', showPhoto: false },
  },
  {
    id: 'preset-relocate-gulf',
    icon: 'globe',
    layout: { style: 'MODERN', color: 'BLACK', showPhoto: true },
  },
];

export const CV_PRESET_IDS = CV_PRESETS.map((preset) => preset.id);

export function getCvPresetById(id?: string | null): CvPreset | undefined {
  if (!id) return undefined;
  return CV_PRESETS.find((preset) => preset.id === id);
}

export function isCvPresetId(id?: string | null): boolean {
  return getCvPresetById(id) !== undefined;
}

/**
 * Search across the localized label and description. The caller passes the
 * already-translated strings so this stays pure and locale-agnostic.
 */
export function filterCvPresets(
  presets: { preset: CvPreset; label: string; description: string }[],
  query: string,
): { preset: CvPreset; label: string; description: string }[] {
  const term = query.trim().toLowerCase();
  if (!term) return presets;
  return presets.filter(
    (entry) =>
      entry.label.toLowerCase().includes(term) ||
      entry.description.toLowerCase().includes(term),
  );
}
